/**
 * =========================================================================================
 * SHERGOLD PRIME ERP - SYSTEM TOKENS & DYNAMIC VARIABLES DICTIONARY
 * =========================================================================================
 *
 * MODULE OVERVIEW:
 * This module defines the dictionary of dynamic data placeholders (tokens) used across 
 * invoice and receipt templates. 
 *
 * Tokens represent variable fields (e.g., {{CustomerName}}, {{TotalAmount}}) that are 
 * dynamically evaluated and populated with actual transaction data during PDF generation.
 *
 * KEY COMPONENTS:
 * 1. TOKEN_CATEGORIES: Organized list of tokens grouped by domain (Customer, Scheme, Financials, Company).
 * 2. SAMPLE_DATA_MAP: Default mock dataset used for live preview rendering in the Designer.
 * 3. TOKEN_ALIASES: Normalization lookup table for mapping variant key names.
 */

import { DEFAULT_PRESET_LOGO } from "./defaultPresetLogo.js";

/**
 * TOKEN_CATEGORIES:
 * Structured taxonomy of supported dynamic placeholders, grouped for easy selection in the UI.
 *
 * Categories:
 * 1. Customer Information: Client identification, contact, and tax metadata.
 * 2. Gold Scheme & Bullion: Savings scheme details, metal weights, rates, and hallmarking.
 * 3. Financials & Payments: Voucher numbers, amounts, tax calculations, and payment modes.
 * 4. Company & Branch Meta: Store trade details, GSTIN, counter metadata, and legal footers.
 */
export const TOKEN_CATEGORIES = [
  {
    id: "sales",
    name: "Jewellery Sales & Items",
    icon: "FiTag",
    tokens: [
      { key: "{{InvoiceNo}}", label: "Invoice Number", sample: "INV-2026-0842", desc: "Tax invoice document identifier" },
      { key: "{{InvoiceDate}}", label: "Invoice Date", sample: "03/10/2026", desc: "Invoice billing date" },
      { key: "{{ItemCode}}", label: "Item / SKU Code", sample: "RNG-22K-104", desc: "Inventory item barcoded code" },
      { key: "{{ItemDescription}}", label: "Item Description", sample: "22K Hallmarked Antique Ring", desc: "Full jewellery ornament name" },
      { key: "{{Purity}}", label: "Gold Purity / Grade", sample: "22K (916)", desc: "Hallmark purity grade (22K, 18K, 24K)" },
      { key: "{{GrossWeight}}", label: "Gross Weight (g)", sample: "5.850", desc: "Total ornament scale weight" },
      { key: "{{StoneWeight}}", label: "Stone / Enamel Wt (g)", sample: "0.350", desc: "Non-gold stone and wax weight" },
      { key: "{{NetWeight}}", label: "Net Gold Weight (g)", sample: "5.500", desc: "Net pure metal billable weight" },
      { key: "{{VA_Percent}}", label: "Value Addition / VA %", sample: "12.0%", desc: "Wastage and craftsmanship percentage" },
      { key: "{{MakingCharges}}", label: "Making Charges (₹)", sample: "3,850.00", desc: "Artisan making fee in currency" },
      { key: "{{RatePerGram}}", label: "Gold Rate / Gram (₹)", sample: "6,950.00", desc: "Applied metal rate per gram" },
      { key: "{{GoldRate}}", label: "Daily 22K Gold Rate", sample: "6,950.00", desc: "Official daily bullion market rate" },
      { key: "{{SilverRate}}", label: "Daily Silver Rate / g", sample: "98.50", desc: "Daily silver bullion rate" },
      { key: "{{HUID}}", label: "BIS Hallmark HUID", sample: "K9A4M2", desc: "Govt 6-character laser mark" },
      { key: "{{HSNCode}}", label: "HSN Code", sample: "7113", desc: "GST tariff code for gold jewellery" },
      { key: "{{Discount}}", label: "Special Discount (₹)", sample: "500.00", desc: "Promotional rebate amount" },
      { key: "{{SubTotal}}", label: "Item Subtotal (₹)", sample: "3,47,229.00", desc: "Gross items value before tax" },
      { key: "{{TaxableAmount}}", label: "Taxable Value (₹)", sample: "3,08,659.00", desc: "Amount subject to GST" },
      { key: "{{CGST}}", label: "CGST Amount 1.5% (₹)", sample: "4,629.89", desc: "Central GST component" },
      { key: "{{SGST}}", label: "SGST Amount 1.5% (₹)", sample: "4,629.89", desc: "State GST component" },
      { key: "{{TotalTax}}", label: "Total GST 3% (₹)", sample: "9,259.78", desc: "Combined 3% GST" },
      { key: "{{RoundOff}}", label: "Round Off (₹)", sample: "-0.78", desc: "Rounding adjustment" },
      { key: "{{GrandTotal}}", label: "Grand Total Payable (₹)", sample: "3,17,918.00", desc: "Net invoice bill amount" }
    ]
  },
  {
    id: "purchase",
    name: "Old Gold & Purchase",
    icon: "FiRefreshCw",
    tokens: [
      { key: "{{PurchaseVoucherNo}}", label: "Purchase Voucher No", sample: "PUR-2026-0158", desc: "Old gold acquisition voucher number" },
      { key: "{{PurchaseDate}}", label: "Purchase Date", sample: "03/10/2026", desc: "Gold acquisition transaction date" },
      { key: "{{OldGoldWeight}}", label: "Old Gold Gross Wt (g)", sample: "28.500", desc: "Received scrap gross weight" },
      { key: "{{DustStoneLoss}}", label: "Dust / Stone Loss (g)", sample: "1.200", desc: "Non-metallic deduction weight" },
      { key: "{{MeltingLoss}}", label: "Melting / Assay Loss (g)", sample: "0.410", desc: "Dross burnt during melt testing" },
      { key: "{{TouchPurity}}", label: "Touch / Fire Assay %", sample: "91.60%", desc: "Assayed gold purity percentage" },
      { key: "{{NetPureGold}}", label: "Net Pure Gold Wt (g)", sample: "24.640", desc: "Computed fine pure gold weight" },
      { key: "{{PurchaseRate}}", label: "Buying Rate / Gram (₹)", sample: "6,680.00", desc: "Agreed purchase rate per gram" },
      { key: "{{OldGoldAmount}}", label: "Old Gold Valuation (₹)", sample: "1,64,595.00", desc: "Assessed old gold gross credit" },
      { key: "{{MeltingCharges}}", label: "Melting & Testing Fee", sample: "250.00", desc: "Deduction for laboratory melting" },
      { key: "{{NetPayable}}", label: "Net Cash/Bank Payable", sample: "1,64,345.00", desc: "Net settlement paid to seller" },
      { key: "{{PaymentMode}}", label: "Settlement Mode", sample: "Bank NEFT / RTGS", desc: "Disbursement payment channel" },
      { key: "{{BankUTR}}", label: "Bank UTR / Ref No", sample: "UTR2026100300941", desc: "Bank wire transaction reference" }
    ]
  },
  {
    id: "customer",
    name: "Customer Information",
    icon: "FiUser",
    tokens: [
      { key: "{{CustomerName}}", label: "Customer Name", sample: "Smt. Priya Sundaram", desc: "Full registered customer name" },
      { key: "{{CustomerCode}}", label: "Customer Code", sample: "CUST-1042", desc: "Unique ERP client identification code" },
      { key: "{{MobileNo}}", label: "Mobile Number", sample: "+91 98450 12345", desc: "Primary WhatsApp / SMS telephone" },
      { key: "{{Address}}", label: "Address Line", sample: "Flat 4B, Emerald Heights, MG Road, Kochi, Kerala - 682016", desc: "Street address and location" },
      { key: "{{PAN}}", label: "PAN Card No", sample: "ABCDE1234F", desc: "Customer tax PAN (Required >= ₹2 Lakh)" },
      { key: "{{AadharNo}}", label: "Aadhaar Card No", sample: "XXXX-XXXX-8921", desc: "Identity proof for gold buyback" },
      { key: "{{CustomerGSTIN}}", label: "Customer GSTIN", sample: "32AAAAA0000A1Z5", desc: "Client GST registration if B2B" },
      { key: "{{StateCode}}", label: "Place of Supply", sample: "32 - Kerala", desc: "State code for GST supply" }
    ]
  },
  {
    id: "scheme",
    name: "Gold Scheme & Bullion",
    icon: "FiAward",
    tokens: [
      { key: "{{SchemeName}}", label: "Scheme Name", sample: "Swarna Nidhi Gold Savings Scheme", desc: "Active investment plan designation" },
      { key: "{{AccountNo}}", label: "Account / Passbook No", sample: "SCH-001042", desc: "Customer passbook account number" },
      { key: "{{InstallmentNo}}", label: "Installment No", sample: "08 / 11", desc: "Current paid installment number" },
      { key: "{{InstallmentAmt}}", label: "Installment Amount", sample: "5,000.00", desc: "Monthly scheme deposit value" },
      { key: "{{OldWeight}}", label: "Previous / Old Weight (g)", sample: "4.574", desc: "Accumulated gold weight before current entry" },
      { key: "{{Weight}}", label: "Current Weight (g)", sample: "1.250", desc: "Current receipt gold weight" },
      { key: "{{TotalWeight}}", label: "Total Accumulated Weight (g)", sample: "5.824", desc: "Total gold saved in passbook to date (Old + Current)" },
      { key: "{{AccumulatedWeight}}", label: "Total Accumulated Wt (g)", sample: "5.824", desc: "Total gold saved in passbook to date" },
      { key: "{{OldBalance}}", label: "Previous / Old Balance (₹)", sample: "35,000.00", desc: "Accumulated ledger balance before entry" },
      { key: "{{TotalAmount}}", label: "Current Receipt Amount (₹)", sample: "5,000.00", desc: "Amount paid in this receipt" },
      { key: "{{ClosingBalance}}", label: "Closing / Total Balance (₹)", sample: "40,000.00", desc: "Updated total ledger monetary balance (Old + Current)" },
      { key: "{{TotalPaidAmount}}", label: "Total Accumulated Amount (₹)", sample: "40,000.00", desc: "Cumulative monetary amount paid to date" }
    ]
  },
  {
    id: "financials",
    name: "Financials & Payments",
    icon: "FiCreditCard",
    tokens: [
      { key: "{{ReceiptNo}}", label: "Receipt / Voucher No", sample: "REC-2026-0001", desc: "System serial voucher number" },
      { key: "{{Date}}", label: "Transaction Date", sample: "03/10/2026", desc: "Voucher entry date (DD/MM/YYYY)" },
      { key: "{{Time}}", label: "Entry Time", sample: "11:45 AM", desc: "Timestamp of receipt printing" },
      { key: "{{TotalAmount}}", label: "Total Amount (₹)", sample: "3,17,918.00", desc: "Monetary amount figure" },
      { key: "{{AmountInWords}}", label: "Amount in Words", sample: "Rupees Three Lakh Seventeen Thousand Nine Hundred Eighteen Only", desc: "Spelled out currency string" },
      { key: "{{BankName}}", label: "Bank Name", sample: "HDFC Bank Ltd", desc: "Store settlement bank name" },
      { key: "{{BankAccountNo}}", label: "Bank Account No", sample: "50200098765432", desc: "Current account number" },
      { key: "{{BankIFSC}}", label: "Bank IFSC Code", sample: "HDFC0000123", desc: "Bank RTGS/NEFT branch IFSC code" },
      { key: "{{BankBranch}}", label: "Bank Branch Name", sample: "MG Road Commercial Branch", desc: "Bank physical branch" }
    ]
  },
  {
    id: "vouchers",
    name: "Receipt & Payment Vouchers",
    icon: "FiCreditCard",
    tokens: [
      { key: "{{VoucherNo}}", label: "Voucher Number", sample: "1 -ADM", desc: "Receipt or payment serial voucher number" },
      { key: "{{AccountNo}}", label: "Account Number", sample: "011", desc: "Ledger account identification code" },
      { key: "{{CustomerName}}", label: "Party / Customer Name", sample: "Smt. Priya Sundaram", desc: "Received from or paid to party name" },
      { key: "{{AmountInWords}}", label: "Amount in Words", sample: "One Thousand Only", desc: "Spelled out transaction amount" },
      { key: "{{PaymentMode}}", label: "Payment / Cheque Mode", sample: "CASH", desc: "Cash / Cheque / DD payment mode" },
      { key: "{{TotalAmount}}", label: "Current Amount (₹)", sample: "1,000.00", desc: "Total transaction monetary amount" },
      { key: "{{Rate}}", label: "Gold Rate / Gram", sample: "6,950.00", desc: "Applied metal bullion rate per gram" },
      { key: "{{OldWeight}}", label: "Previous / Old Weight (gm)", sample: "1.107 gm", desc: "Accumulated bullion weight before this entry" },
      { key: "{{Weight}}", label: "Weight (gm)", sample: "0.143 gm", desc: "Transaction row metal weight" },
      { key: "{{TotalWeight}}", label: "Total Accumulated Weight (gm)", sample: "1.250 gm", desc: "Cumulative passbook total bullion weight (Old + Current)" },
      { key: "{{AgentName}}", label: "Agent / Collector Name", sample: "Rahul K", desc: "Scheme agent or collection staff name" },
      { key: "{{OldBalance}}", label: "Previous / Old Balance (₹)", sample: "35,000.00", desc: "Accumulated ledger balance before this entry" },
      { key: "{{ClosingBalance}}", label: "Closing Balance (₹)", sample: "36,000.00", desc: "Updated total ledger balance after this entry (Old + Current)" },
      { key: "{{TotalPaidAmount}}", label: "Total Accumulated Amount (₹)", sample: "36,000.00", desc: "Cumulative total amount paid to date" },
      { key: "{{CashAccount}}", label: "Cash Ledger Account", sample: "CASH IN HAND", desc: "Cash or bank account credited/debited" },
      { key: "{{SalesMan}}", label: "Sales Man / Biller", sample: "Arun M", desc: "Billing staff or salesman name" },
      { key: "{{SchemeCode}}", label: "Scheme ID / Code", sample: "SCH-001", desc: "Gold savings scheme identifier code" },
      { key: "{{SchemeName}}", label: "Scheme Name", sample: "Swarna Nidhi Gold Savings", desc: "Gold savings plan name" }
    ]
  },
  {
    id: "company",
    name: "Company & Branch Meta",
    icon: "FiHome",
    tokens: [
      { key: "{{CompanyName}}", label: "Company / Shop Name", sample: "SHERSOFT", desc: "Registered jewelry business trade name" },
      { key: "{{BranchName}}", label: "Branch / Counter", sample: "Main Flagship Showroom", desc: "Specific showroom or outlet" },
      { key: "{{BranchAddress}}", label: "Branch Address", sample: "Showroom No. 12, Gold Souk Market Road, City Center - 682016", desc: "Full shop address" },
      { key: "{{GSTIN}}", label: "Company GSTIN", sample: "32AAAAA0000A1Z5", desc: "15-digit GST registration code" },
      { key: "{{Phone}}", label: "Showroom Telephone", sample: "+91 484 2345678 / +91 98460 00000", desc: "Customer care / billing contact" },
      { key: "{{ShowroomEmail}}", label: "Email Address", sample: "contact@shergoldjewellers.com", desc: "Customer support email" },
      { key: "{{ShowroomWebsite}}", label: "Showroom Website", sample: "www.shergoldjewellers.com", desc: "Official web portal" },
      { key: "{{UPI_ID}}", label: "Store UPI VPA", sample: "shergold@upi", desc: "UPI ID for QR code payment generation" },
      { key: "{{CashierName}}", label: "Cashier / Staff Name", sample: "K. Rahul (Counter 02)", desc: "Operator creating transaction" },
      { key: "{{AuthorizedSignatory}}", label: "Signatory Label", sample: "For SherGold Jewellers", desc: "Legal signature footer text" }
    ]
  }
];

/**
 * SAMPLE_DATA_MAP:
 * Key-value mapping of sample data used for rendering realistic preview PDFs during design time.
 * Supports both braced (e.g., "{{CustomerName}}") and unbraced ("CustomerName") formats.
 */
export const SAMPLE_DATA_MAP = TOKEN_CATEGORIES.reduce((acc, cat) => {
  cat.tokens.forEach((t) => {
    acc[t.key] = t.sample;
    const cleanKey = t.key.replace(/[{}]/g, "");
    acc[cleanKey] = t.sample;
  });
  return acc;
}, {});

/**
 * TOKEN_ALIASES:
 * Normalization table mapping alternative dynamic variable names to canonical keys.
 * Ensures backward compatibility with legacy formats (e.g., SMS template syntax ${NetWt}).
 */
const TOKEN_ALIASES = {
  showroomname: "companyname",
  shopname: "companyname",
  netwt: "netweight",
  grosswt: "grossweight",
  gm: "grossweight",
  total: "totalamount",
  grandtotal: "grandtotal",
  accno: "accountno",
  accountnumber: "accountno",
  custname: "customername",
  mobile: "mobileno",
  mobileno: "mobileno",
  date: "date",
  invoiceno: "invoiceno",
  invoicedate: "invoicedate",
  itemname: "itemdescription",
  itemdesc: "itemdescription",
  rate: "rate",
  goldrate: "goldrate",
  voucherno: "voucherno",
  receiptno: "voucherno",
  paymentno: "voucherno",
  entryno: "voucherno",
  oldweight: "oldweight",
  oldwt: "oldweight",
  previousweight: "oldweight",
  weight: "weight",
  totalweight: "totalweight",
  accumulatedwt: "totalweight",
  accumulatedweight: "totalweight",
  paymentmode: "paymentmode",
  partyname: "customername",
  // Scheme Receipt specific aliases
  agentname: "agentname",
  agent: "agentname",
  oldbalance: "oldbalance",
  previousbalance: "oldbalance",
  ob: "oldbalance",
  closingbalance: "closingbalance",
  totalpaidamount: "closingbalance",
  totalpaid: "closingbalance",
  totalbalance: "closingbalance",
  cumulativetotal: "closingbalance",
  cashaccount: "cashaccount",
  cashacc: "cashaccount",
  salesman: "salesman",
  schemecode: "schemecode",
  schemename: "schemename",
  upiid: "upi_id",
  upi: "upi_id",
  upivpa: "upi_id",
  showroomupi: "upi_id"
};


/**
 * resolveTemplateInputs:
 * Evaluates template schemas against transaction or sample data, substituting 
 * dynamic variable placeholders with actual formatted strings or structured objects.
 *
 * Features:
 * - Supports both double-brace `{{Token}}` and dollar-brace `${Token}` syntax.
 * - Performs case-insensitive matching and alias resolution.
 * - Handles tabular schemas by serializing row data.
 * - Preserves static text elements.
 *
 * @param {Object} template - Template definition containing schema definitions and dimensions.
 * @param {Object} data - Context object containing transaction field values.
 * @returns {Array<Object>} Processed schema input arrays ready for @pdfme/generator.
 */
export function resolveTemplateInputs(template, data = {}) {
  if (!template || !template.schemas) return [{}];

  const mergedData = { ...SAMPLE_DATA_MAP, ...data };

  const lookup = {};
  Object.keys(mergedData).forEach((k) => {
    const cleanKey = k.replace(/[{}$]/g, "").toLowerCase();
    lookup[cleanKey] = mergedData[k];
  });

  const getValue = (rawKey) => {
    const clean = rawKey.replace(/[{}$]/g, "").toLowerCase();
    if (lookup[clean] !== undefined) return String(lookup[clean]);
    const aliased = TOKEN_ALIASES[clean];
    if (aliased && lookup[aliased] !== undefined) return String(lookup[aliased]);
    return null;
  };

  const regex = /(?:\{\{|\$\{)([\w_]+)(?:\}\}|\})/g;
  const pageInputs = [];

  template.schemas.forEach((pageSchemas) => {
    const inputObj = {};

    pageSchemas.forEach((schema) => {
      if (schema.type === "table") {
        if (data[schema.name]) {
          inputObj[schema.name] = typeof data[schema.name] === "string"
            ? data[schema.name]
            : JSON.stringify(data[schema.name]);
        } else if (data.items && Array.isArray(data.items)) {
          inputObj[schema.name] = JSON.stringify(data.items);
        } else if (typeof schema.content === "string") {
          inputObj[schema.name] = schema.content;
        } else {
          inputObj[schema.name] = JSON.stringify([]);
        }
      } else {
        const nameVal = getValue(schema.name);
        let finalVal = null;
        if (nameVal !== null) {
          finalVal = nameVal;
        } else if (typeof schema.content === "string" && (schema.content.includes("{{") || schema.content.includes("${"))) {
          finalVal = schema.content.replace(regex, (match, key) => {
            const val = getValue(key);
            return val !== null ? val : match;
          });
        } else if (typeof schema.content === "string") {
          finalVal = schema.content;
        }

        // 🖼️ Safeguard for Image Schemas: Prevent 'SOI not found in JPEG' by ensuring PNG/JPEG
        if (schema.type === "image" && typeof finalVal === "string") {
          if (finalVal.includes("<svg") || finalVal.startsWith("data:image/svg")) {
            finalVal = DEFAULT_PRESET_LOGO;
          }
        }

        // 📱 Smart Normalization for QR Codes (UPI & Payment links)
        if (schema.type === "qrcode" && typeof finalVal === "string") {
          const trimmed = finalVal.trim();
          // Case 1: Raw UPI ID entered (e.g. "myname@upi" or "store@okhdfcbank")
          if (trimmed.includes("@") && !trimmed.startsWith("upi://") && !trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
            finalVal = `upi://pay?pa=${trimmed}&cu=INR`;
          }
          // Case 2: Full UPI URL (e.g. "upi://pay?pa=...&am=...")
          else if (trimmed.startsWith("upi://pay")) {
            // Strip commas, currency symbols, and spaces from am= (amount) parameter
            finalVal = trimmed.replace(/([?&]am=)([^&]+)/gi, (match, prefix, amt) => {
              const cleanAmt = amt.replace(/[₹$,\s]/g, "");
              const parsed = parseFloat(cleanAmt);
              return `${prefix}${!isNaN(parsed) ? parsed.toFixed(2) : cleanAmt}`;
            });

            // Ensure spaces in payee name (&pn=) are URL encoded
            finalVal = finalVal.replace(/([?&]pn=)([^&]+)/gi, (match, prefix, name) => {
              return `${prefix}${encodeURIComponent(decodeURIComponent(name))}`;
            });

            // Ensure cu=INR parameter is present
            if (!finalVal.includes("cu=")) {
              finalVal += "&cu=INR";
            }
          }
        }

        if (finalVal !== null) {
          inputObj[schema.name] = finalVal;
        }
      }
    });

    pageInputs.push(inputObj);
  });

  return pageInputs.length > 0 ? pageInputs : [{}];
}
